import { useState } from "react";
import DetailsBody from "./DetailsBody";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { getSourceColor, getSourceLabel, METRIC_LABELS, METRIC_MAX } from "../utils/cvss";
import ColorVector from "./ColorVector";

const METRICS = ["AV", "AC", "PR", "UI", "S", "C", "I", "A"];
const CIA_METRICS = new Set(["C", "I", "A"]);

function CvssRadarChart({ cveData, visibleSources }) {
  const [hiddenSources, setHiddenSources] = useState(new Set());
  const [hideCIA, setHideCIA] = useState(false);
  const [detailsSource, setDetailsSource] = useState(null);

  if (!cveData || !cveData.scores) return null;

  const availableSources = visibleSources.filter(
    (s) => cveData.scores[s]?.components
  );
  if (availableSources.length === 0) {
    return <div className="na-cell">No component data available</div>;
  }

  const shownSources = availableSources.filter((s) => !hiddenSources.has(s));

  const toggleSource = (src) => {
    setHiddenSources((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next;
    });
  };

  const activeMetrics = hideCIA ? METRICS.filter((m) => !CIA_METRICS.has(m)) : METRICS;

  // Build data for radar: one entry per metric, normalized to 0-100%
  const data = activeMetrics.map((metric) => {
    const entry = { metric: METRIC_LABELS[metric] || metric, fullWidth: 100 };
    for (const src of availableSources) {
      const comp = cveData.scores[src].components.find(
        (c) => c.metric === metric
      );
      if (comp) {
        // Normalize to percentage of max for uniform radar axes
        entry[src] = Math.round((comp.numeric / METRIC_MAX[metric]) * 100);
        entry[`${src}_label`] = `${comp.label} (${comp.short})`;
      } else {
        entry[src] = 0;
      }
    }
    return entry;
  });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const metric = payload[0]?.payload?.metric;
    return (
      <div
        style={{
          background: "#21262d",
          border: "1px solid #30363d",
          borderRadius: 6,
          padding: "8px 12px",
          fontSize: 12,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{metric}</div>
        {payload.map((p) => (
          <div key={p.dataKey} style={{ color: p.stroke }}>
            {getSourceLabel(p.dataKey)}:{" "}
            {p.payload[`${p.dataKey}_label`] || `${p.value}%`}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="radar-panel">
      <div className="radar-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={data} cx="50%" cy="50%" outerRadius="75%">
            <PolarGrid stroke="#30363d" />
            <PolarAngleAxis
              dataKey="metric"
              tick={{ fill: "#8b949e", fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />
            {shownSources.map((src) => (
              <Radar
                key={src}
                name={getSourceLabel(src)}
                dataKey={src}
                stroke={getSourceColor(src)}
                fill={getSourceColor(src)}
                fillOpacity={0.1}
                strokeWidth={2}
              />
            ))}
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="radar-legend">
        {availableSources.map((src) => {
          const info = cveData.scores[src];
          const visible = !hiddenSources.has(src);
          const hasDetails = !!info.details;
          return (
            <div key={src} className="radar-legend-item radar-toggle">
              <input
                type="checkbox"
                checked={visible}
                onChange={() => toggleSource(src)}
                className="radar-checkbox"
              />
              <span
                className="radar-legend-dot"
                style={{ background: visible ? getSourceColor(src) : "#6e7681" }}
              />
              <span className="radar-legend-source" style={{ opacity: visible ? 1 : 0.5 }}>
                {getSourceLabel(src)}
              </span>
            </div>
          );
        })}
        <label className="radar-legend-item radar-toggle" style={{ marginTop: 12 }}>
          <input
            type="checkbox"
            checked={hideCIA}
            onChange={() => setHideCIA((v) => !v)}
            className="radar-checkbox"
          />
          <span style={{ fontSize: 12, color: "#8b949e" }}>
            Hide C / I / A
          </span>
        </label>
        <div style={{ marginTop: 8, fontSize: 12, color: "#6e7681" }}>
          Axes show relative severity (0-100%). Higher = more severe.
        </div>
      </div>

      {detailsSource && cveData.scores[detailsSource]?.details && (
        <div className="details-inline">
          <div className="details-inline-header">
            <span
              className="details-inline-dot"
              style={{ background: getSourceColor(detailsSource) }}
            />
            <span style={{ fontWeight: 700 }}>
              {getSourceLabel(detailsSource)}
            </span>
            <span style={{ color: "#8b949e" }}>Justification</span>
            <button className="details-inline-close" onClick={() => setDetailsSource(null)}>
              &times;
            </button>
          </div>
          <DetailsBody text={cveData.scores[detailsSource].details} />
        </div>
      )}
    </div>
  );
}

export default CvssRadarChart;
